import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { integrationQueries } from '@/features/integrations/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const NewIntegration = lazy(
	() => import('@/features/integrations/components/new-integration/'),
)

export const Route = createFileRoute('/_dashboard/integrations/new/$id')({
	beforeLoad: () => roleAuthGuard(ROLES.SystemManager),

	loader: ({ context: { queryClient }, params }) => {
		const { id } = params
		const tenant = getCurrentTenant()

		if (tenant) {
			queryClient.prefetchQuery(integrationQueries.definition(tenant, id))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'New Integration | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { id } = Route.useParams()

	return (
		<ErrorBoundary
			feature="integrations"
			section="new-integration"
			entityId={id}
		>
			<Suspense fallback={<NewIntegrationSkeleton />}>
				<NewIntegration integrationId={id} />
			</Suspense>
		</ErrorBoundary>
	)
}

function RoutePendingComponent() {
	return <NewIntegrationSkeleton />
}

function NewIntegrationSkeleton() {
	return (
		<div className="flex flex-col gap-8">
			<Skeleton className="h-24 w-full" />

			<div className="flex w-full flex-col items-start justify-between gap-8 sm:max-w-7xl sm:flex-row">
				<Skeleton className="h-[400px] w-full max-w-screen-sm" />
				<Skeleton className="h-[200px] w-full max-w-screen-sm" />
			</div>
		</div>
	)
}
